import Editor from '@monaco-editor/react';
import { Code as CodeIcon, Refresh as RefreshIcon, Send as SendIcon, Sort } from '@mui/icons-material';
import {
    Alert,
    alpha,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Switch,
    Tab,
    Tabs,
    TextField,
    Theme,
    Toolbar,
    Typography,
    useTheme,
} from '@mui/material';
import { useFormik } from 'formik';
import React, { useContext, useEffect, useState } from 'react';
import * as Yup from 'yup';
import PageContainer from '../../components/common/PageContainter';
import useAxios from '../../hooks/useAxios';
import { SettingsContext } from '../../store/SettingsContext';
import { UserContext } from '../../store/UserContext';

interface ProxyConfig {
    host: string;
    port: number;
    auth?: {
        username: string;
        password: string;
    };
    protocol?: 'http' | 'https';
}

interface ApiRequestData {
    username: string;
    method: string;
    endpoint: string;
    useProxy: boolean;
    proxyConfig?: ProxyConfig;
    data?: object;
    params?: object;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

// OnlyFans API endpoints extracted from the class
const API_ENDPOINTS = [
    // Authentication & User Info
    'https://onlyfans.com/api2/v2/users/otp/phone',
    'https://texts.onlyfans.com/init.json',
    'https://onlyfans.com/api2/v2/users/me/id',
    'https://onlyfans.com/api2/v2/ip',
    'https://onlyfans.com/api2/v2/users/me',
    'https://onlyfans.com/api2/v2/users/ws-auth',
    'https://onlyfans.com/api2/v2/users/me/start-date-model',
    'https://onlyfans.com/api2/v2/users/logout',

    // User Management
    'https://onlyfans.com/api2/v2/users/{username}',
    'https://onlyfans.com/api2/v2/subscriptions/{user_id}',
    'https://onlyfans.com/api2/v2/users/list',
    'https://onlyfans.com/api2/v2/users/{user_id}/subscribe',
    'https://onlyfans.com/api2/v2/users/{user_id}/restrict',
    'https://onlyfans.com/api2/v2/users/{user_id}/block',
    'https://onlyfans.com/api2/v2/users/restrict',
    'https://onlyfans.com/api2/v2/users/blocked',

    // Subscriptions & Followers
    'https://onlyfans.com/api2/v2/subscriptions/count/all',
    'https://onlyfans.com/api2/v2/subscriptions/subscribers/count',
    'https://onlyfans.com/api2/v2/subscriptions/subscribes/count',
    'https://onlyfans.com/api2/v2/subscriptions/subscribers',
    'https://onlyfans.com/api2/v2/subscriptions/subscribes',
    'https://onlyfans.com/api2/v2/subscriptions/{user_id}/discount',

    // Posts & Content
    'https://onlyfans.com/api2/v2/posts',
    'https://onlyfans.com/api2/v2/posts/{post_id}',
    'https://onlyfans.com/api2/v2/posts/{post_id}/archive',
    'https://onlyfans.com/api2/v2/posts/{post_id}/pin',
    'https://onlyfans.com/api2/v2/posts/{post_id}/stats',
    'https://onlyfans.com/api2/v2/posts/{post_id}/buyers',
    'https://onlyfans.com/api2/v2/posts/{post_id}/tips',
    'https://onlyfans.com/api2/v2/posts/{post_id}/favorites',
    'https://onlyfans.com/api2/v2/users/{user_id}/posts',
    'https://onlyfans.com/api2/v2/users/{user_id}/posts/medias',

    // Stories
    'https://onlyfans.com/api2/v2/users/me/stories',
    'https://onlyfans.com/api2/v2/stories',
    'https://onlyfans.com/api2/v2/stories/{story_id}',

    // Chat & Messages
    'https://onlyfans.com/api2/v2/chats',
    'https://onlyfans.com/api2/v2/chats/{user_id}',
    'https://onlyfans.com/api2/v2/chats/{user_id}/messages',
    'https://onlyfans.com/api2/v2/chats/{user_id}/messages/{queue_id}',
    'https://onlyfans.com/api2/v2/chats/{user_id}/media',
    'https://onlyfans.com/api2/v2/chats/{user_id}/media/photos',
    'https://onlyfans.com/api2/v2/chats/{user_id}/media/videos',
    'https://onlyfans.com/api2/v2/chats/{user_id}/media/audios',
    'https://onlyfans.com/api2/v2/chats/{user_id}/mark-as-read',
    'https://onlyfans.com/api2/v2/chats/{user_id}/mute',
    'https://onlyfans.com/api2/v2/messages/{message_id}',
    'https://onlyfans.com/api2/v2/messages/queue',
    'https://onlyfans.com/api2/v2/messages/queue/stats',
    'https://onlyfans.com/api2/v2/messages/queue/{queue_id}',
    'https://onlyfans.com/api2/v2/messages/queue/{queue_id}/buyers',

    // Lists
    'https://onlyfans.com/api2/v2/lists',
    'https://onlyfans.com/api2/v2/lists/{list_id}',
    'https://onlyfans.com/api2/v2/lists/{list_id}/users',
    'https://onlyfans.com/api2/v2/lists/{list_id}/users/{user_id}',
    'https://onlyfans.com/api2/v2/lists/users',
    'https://onlyfans.com/api2/v2/lists/sort',
    'https://onlyfans.com/api2/v2/lists/fans/sort',
    'https://onlyfans.com/api2/v2/lists/recent/users',
    'https://onlyfans.com/api2/v2/lists/rebill_on/users',
    'https://onlyfans.com/api2/v2/lists/rebill_off/users',
    'https://onlyfans.com/api2/v2/lists/muted/users',
    'https://onlyfans.com/api2/v2/lists/friends/users',
    'https://onlyfans.com/api2/v2/lists/tagged/users',
    'https://onlyfans.com/api2/v2/lists/{list_id}/users/queue/{mass_message_id}/buyers',

    // Labels
    'https://onlyfans.com/api2/v2/labels',
    'https://onlyfans.com/api2/v2/labels/{label_id}/posts',

    // Vault
    'https://onlyfans.com/api2/v2/vault/lists',
    'https://onlyfans.com/api2/v2/vault/lists/{album_id}',
    'https://onlyfans.com/api2/v2/vault/media',
    'https://onlyfans.com/api2/v2/vault/media/hidden',
    'https://onlyfans.com/api2/v2/vault/media/{media_id}/release-forms',
    'https://onlyfans.com/api2/v2/vault/media/release-forms',

    // Notifications
    'https://onlyfans.com/api2/v2/users/notifications',
    'https://onlyfans.com/api2/v2/users/notifications/{id}/read',
    'https://onlyfans.com/api2/v2/release-forms/mentions',

    // Earnings & Payouts
    'https://onlyfans.com/api2/v2/payouts/stats',
    'https://onlyfans.com/api2/v2/payouts/requests',
    'https://onlyfans.com/api2/v2/payouts/transactions',
    'https://onlyfans.com/api2/v2/payouts/chargebacks',

    // Upload & Media
    'https://onlyfans.com/api2/v2/upload/signed/create',

    // Campaigns & Tracking
    'https://onlyfans.com/api2/v2/campaigns',
    'https://onlyfans.com/api2/v2/campaigns/{campaign_id}',
    'https://onlyfans.com/api2/v2/campaigns/{campaign_id}/claimers',

    // Trials & Promotions
    'https://onlyfans.com/api2/v2/trials',
    'https://onlyfans.com/api2/v2/trials/{trial_id}',
    'https://onlyfans.com/api2/v2/trials/stats',
    'https://onlyfans.com/api2/v2/promotions-trials',

    // Release Forms & Tagged Friends
    'https://onlyfans.com/api2/v2/posts/tagged-friend-users',
    'https://onlyfans.com/api2/v2/release-forms',

    // Live Streaming
    'https://onlyfans.com/api2/v2/streams/{stream_id}',
    'https://onlyfans.com/api2/v2/streams/{stream_id}/block',
    'https://onlyfans.com/api2/v2/streams/{stream_id}/comments',
    'https://onlyfans.com/api2/v2/streams/{stream_id}/comments/{comment_id}',
    'https://onlyfans.com/api2/v2/streams/{stream_id}/comments/{comment_id}/pin',
    'https://onlyfans.com/api2/v2/streams/{stream_id}/stats',
    'https://onlyfans.com/api2/v2/streams/{stream_id}/viewers',
    'https://onlyfans.com/api2/v2/streams/{stream_id}/voters/{poll_id}',

    // Sessions & Settings
    'https://onlyfans.com/api2/v2/sessions',
    'https://onlyfans.com/api2/v2/users/me/settings',
    'https://onlyfans.com/api2/v2/users/me/settings/security',
    'https://onlyfans.com/api2/v2/users/settings/notifications/transports',
    'https://onlyfans.com/api2/v2/users/settings/notifications/{transport}',

    // Statistics
    'https://onlyfans.com/api2/v2/users/me/stats/overview',
    'https://onlyfans.com/api2/v2/users/me/stats/messages/bought',
    'https://onlyfans.com/api2/v2/subscriptions/subscribers/latest',

    // Logging
    'https://onlyfans.com/api2/v2/log',
];

const TabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
        <div role="tabpanel" hidden={value !== index} id={`request-tabpanel-${index}`} aria-labelledby={`request-tab-${index}`} {...other}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
};

const validationSchema = Yup.object({
    username: Yup.string().required('Username is required'),
    method: Yup.string().required('Method is required'),
    endpoint: Yup.string()
        .required('Endpoint is required')
        .matches(/^https:\/\/onlyfans\.com\/api2\/v2/, 'Endpoint must start with https://onlyfans.com/api2/v2'),
    useProxy: Yup.boolean(),
    proxyConfig: Yup.object().when('useProxy', {
        is: true,
        then: () =>
            Yup.object({
                host: Yup.string(),
                port: Yup.number().min(1).max(65535),
                protocol: Yup.string().oneOf(['http', 'https']),
                auth: Yup.object({
                    username: Yup.string(),
                    password: Yup.string(),
                }),
            }),
    }),
});

// utility
const sortObjectKeys = (obj: any) =>
    Object.keys(obj)
        .sort()
        .reduce((acc: any, key) => {
            acc[key] = typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key]) ? sortObjectKeys(obj[key]) : obj[key];
            return acc;
        }, {});

const filterObjectByText = (obj: any, filter: string) => {
    if (!filter) return obj;
    const lowerFilter = filter.toLowerCase();
    const result: any = {};
    for (const [key, value] of Object.entries(obj)) {
        if (key.toLowerCase().includes(lowerFilter)) {
            result[key] = value;
        } else if (typeof value === 'object' && value !== null) {
            const filtered = filterObjectByText(value, filter);
            if (Object.keys(filtered).length > 0) result[key] = filtered;
        }
    }
    return result;
};

const AdminOnlyFansApiTester = () => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const theme: Theme = useTheme();
    const axios = useAxios();

    const [loading, setLoading] = useState(false);
    const [response, setResponse] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [dataJson, setDataJson] = useState('{\n  \n}');
    const [paramsJson, setParamsJson] = useState('{\n  \n}');
    const [dataError, setDataError] = useState<string | null>(null);
    const [paramsError, setParamsError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState(0);
    const [responseTab, setResponseTab] = useState(0);

    // state for sorting and filtering
    const [sortJson, setSortJson] = useState(() => {
        const saved = localStorage.getItem('sortJson');
        return saved ? saved === 'true' : true;
    });
    const [filterText, setFilterText] = useState('');

    useEffect(() => {
        localStorage.setItem('sortJson', sortJson.toString());
    }, [sortJson]);

    const renderResponse = () => {
        try {
            let json = response;
            if (sortJson) json = sortObjectKeys(json);
            if (filterText.trim()) json = filterObjectByText(json, filterText.trim());
            return JSON.stringify(json, null, 2);
        } catch {
            return '// Failed to render JSON';
        }
    };

    const formik = useFormik({
        initialValues: {
            username: '',
            method: 'GET',
            endpoint: 'https://onlyfans.com/api2/v2/',
            useProxy: true,
            proxyConfig: {
                host: '',
                port: 8080,
                protocol: 'http' as 'http' | 'https',
                auth: {
                    username: '',
                    password: '',
                },
            },
        },
        validationSchema,
        onSubmit: async values => {
            setLoading(true);
            setError(null);
            setResponse(null);

            try {
                const requestData: ApiRequestData = {
                    username: values.username,
                    method: values.method,
                    endpoint: values.endpoint,
                    useProxy: values.useProxy,
                };

                if (values.useProxy) {
                    if (values.proxyConfig.host) {
                        requestData.proxyConfig = {
                            host: values.proxyConfig.host,
                            port: values.proxyConfig.port,
                            protocol: values.proxyConfig.protocol,
                        };

                        if (values.proxyConfig.auth?.username) {
                            requestData.proxyConfig.auth = {
                                username: values.proxyConfig.auth.username,
                                password: values.proxyConfig.auth.password,
                            };
                        }
                    }
                }

                // Parse and validate JSON data
                if (dataJson.trim() && dataJson.trim() !== '{\n  \n}') {
                    try {
                        requestData.data = JSON.parse(dataJson);
                    } catch (err) {
                        setError('Invalid JSON in request data');
                        setLoading(false);
                        return;
                    }
                }

                // Parse and validate JSON params
                if (paramsJson.trim() && paramsJson.trim() !== '{\n  \n}') {
                    try {
                        requestData.params = JSON.parse(paramsJson);
                    } catch (err) {
                        setError('Invalid JSON in query parameters');
                        setLoading(false);
                        return;
                    }
                }

                const apiResponse = await axios.post(settingsContext.routes.system.onlyFansApiTester, requestData);
                setResponse(apiResponse.data);
                setResponseTab(0); // Switch to response tab
            } catch (err: any) {
                setError(err.response?.data?.error || err.message || 'An error occurred');
                setResponseTab(0); // Switch to response tab to show error
            } finally {
                setLoading(false);
            }
        },
    });

    const handleResetForm = () => {
        formik.resetForm();
        setResponse(null);
        setError(null);
        setDataJson('{\n  \n}');
        setParamsJson('{\n  \n}');
        setDataError(null);
        setParamsError(null);
    };

    const validateJson = (value: string): boolean => {
        if (!value.trim() || value.trim() === '{\n  \n}') return true;
        try {
            JSON.parse(value);
            return true;
        } catch {
            return false;
        }
    };

    const handleDataChange = (value: string | undefined) => {
        const newValue = value || '';
        setDataJson(newValue);
        setDataError(validateJson(newValue) ? null : 'Invalid JSON format');
    };

    const handleParamsChange = (value: string | undefined) => {
        const newValue = value || '';
        setParamsJson(newValue);
        setParamsError(validateJson(newValue) ? null : 'Invalid JSON format');
    };

    if (!userContext.isAdmin) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    backgroundColor: '#f8f9fa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3,
                }}
            >
                <Paper
                    sx={{
                        p: 4,
                        maxWidth: 400,
                        textAlign: 'center',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        Access Denied
                    </Typography>
                    <Typography variant="body1">You do not have permission to access this page.</Typography>
                </Paper>
            </Box>
        );
    }

    return (
        <PageContainer>
            {/* Top Toolbar */}
            <Paper
                sx={{
                    borderRadius: 0,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    backgroundColor: '#fff',
                }}
            >
                <Toolbar sx={{ px: 3 }}>
                    <CodeIcon sx={{ mr: 2, color: 'primary.main' }} />
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main', flexGrow: 1 }}>
                        OnlyFans API Tester
                    </Typography>
                    <Button variant="outlined" onClick={handleResetForm} disabled={loading} startIcon={<RefreshIcon />} sx={{ mr: 2 }}>
                        Reset
                    </Button>
                    <Button
                        onClick={() => formik.handleSubmit()}
                        variant="contained"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                        sx={{ minWidth: 120 }}
                    >
                        {loading ? 'Sending...' : 'Send'}
                    </Button>
                </Toolbar>
            </Paper>

            <Box sx={{ display: 'flex', height: 'calc(100vh - 164px)' }}>
                {/* Left Panel - Request Configuration */}
                <Box
                    sx={{
                        width: '50%',
                        borderRight: 1,
                        borderColor: 'divider',
                        backgroundColor: 'white',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {/* Request URL Bar */}
                    <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
                        <form onSubmit={formik.handleSubmit}>
                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <FormControl sx={{ minWidth: 120 }}>
                                    <Select
                                        name="method"
                                        value={formik.values.method}
                                        onChange={formik.handleChange}
                                        size="small"
                                        sx={{
                                            '& .MuiSelect-select': {
                                                fontWeight: 'bold',
                                                display: 'flex',
                                                alignItems: 'center',
                                            },
                                        }}
                                    >
                                        <MenuItem value="GET">
                                            <Chip label="GET" size="small" color="success" variant="filled" />
                                        </MenuItem>
                                        <MenuItem value="POST">
                                            <Chip label="POST" size="small" color="warning" variant="filled" />
                                        </MenuItem>
                                        <MenuItem value="PUT">
                                            <Chip label="PUT" size="small" color="info" variant="filled" />
                                        </MenuItem>
                                        <MenuItem value="DELETE">
                                            <Chip label="DELETE" size="small" color="error" variant="filled" />
                                        </MenuItem>
                                        <MenuItem value="PATCH">
                                            <Chip label="PATCH" size="small" color="secondary" variant="filled" />
                                        </MenuItem>
                                    </Select>
                                </FormControl>

                                <Autocomplete
                                    fullWidth
                                    freeSolo
                                    options={API_ENDPOINTS}
                                    value={formik.values.endpoint}
                                    onInputChange={(_, newValue) => {
                                        formik.setFieldValue('endpoint', newValue || '');
                                    }}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            name="endpoint"
                                            placeholder="Enter request URL or select from suggestions"
                                            error={formik.touched.endpoint && Boolean(formik.errors.endpoint)}
                                            size="small"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: 'grey.50',
                                                },
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                    {option}
                                                </Typography>
                                                {option.includes('{') && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Replace {option.match(/\{[^}]+\}/g)?.join(', ')} with actual values
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    )}
                                    filterOptions={(options, { inputValue }) => {
                                        // Custom filtering to match partial paths
                                        return options.filter(
                                            option =>
                                                option.toLowerCase().includes(inputValue.toLowerCase()) ||
                                                option.split('/').some(segment => segment.toLowerCase().includes(inputValue.toLowerCase())),
                                        );
                                    }}
                                />
                            </Box>

                            <TextField
                                fullWidth
                                name="username"
                                label="Username"
                                value={formik.values.username}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.username && Boolean(formik.errors.username)}
                                helperText={formik.touched.username && formik.errors.username}
                                size="small"
                                required
                            />
                        </form>
                    </Box>

                    {/* Request Configuration Tabs */}
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Tabs
                            value={activeTab}
                            onChange={(_, newValue) => setActiveTab(newValue)}
                            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
                        >
                            <Tab label="Body" />
                            <Tab label="Params" />
                            <Tab label="Proxy" />
                        </Tabs>

                        <TabPanel value={activeTab} index={0}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'medium' }}>
                                Request Body (JSON)
                            </Typography>
                            {dataError && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {dataError}
                                </Alert>
                            )}
                            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, height: 300 }}>
                                <Editor
                                    height="100%"
                                    defaultLanguage="json"
                                    value={dataJson}
                                    onChange={handleDataChange}
                                    options={{
                                        minimap: { enabled: false },
                                        scrollBeyondLastLine: false,
                                        fontSize: 14,
                                        wordWrap: 'on',
                                        automaticLayout: true,
                                        tabSize: 2,
                                    }}
                                    theme="vs-light"
                                />
                            </Box>
                        </TabPanel>

                        <TabPanel value={activeTab} index={1}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'medium' }}>
                                Query Parameters (JSON)
                            </Typography>
                            {paramsError && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {paramsError}
                                </Alert>
                            )}
                            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, height: 300 }}>
                                <Editor
                                    height="100%"
                                    defaultLanguage="json"
                                    value={paramsJson}
                                    onChange={handleParamsChange}
                                    options={{
                                        minimap: { enabled: false },
                                        scrollBeyondLastLine: false,
                                        fontSize: 14,
                                        wordWrap: 'on',
                                        automaticLayout: true,
                                        tabSize: 2,
                                    }}
                                    theme="vs-light"
                                />
                            </Box>
                        </TabPanel>

                        <TabPanel value={activeTab} index={2}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formik.values.useProxy}
                                            onChange={formik.handleChange}
                                            name="useProxy"
                                            color="primary"
                                        />
                                    }
                                    label="Enable Proxy"
                                />

                                {formik.values.useProxy && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <TextField
                                            fullWidth
                                            name="proxyConfig.host"
                                            label="Proxy Host (Optional)"
                                            value={formik.values.proxyConfig.host}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            size="small"
                                        />

                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <TextField
                                                name="proxyConfig.port"
                                                label="Port"
                                                type="number"
                                                value={formik.values.proxyConfig.port}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                size="small"
                                                sx={{ flex: 1 }}
                                            />

                                            <FormControl sx={{ flex: 1 }} size="small">
                                                <InputLabel>Protocol</InputLabel>
                                                <Select
                                                    name="proxyConfig.protocol"
                                                    value={formik.values.proxyConfig.protocol}
                                                    onChange={formik.handleChange}
                                                    label="Protocol"
                                                >
                                                    <MenuItem value="http">HTTP</MenuItem>
                                                    <MenuItem value="https">HTTPS</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Box>

                                        <Typography variant="subtitle2" sx={{ fontWeight: 'medium', mt: 1 }}>
                                            Authentication (Optional)
                                        </Typography>

                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <TextField
                                                name="proxyConfig.auth.username"
                                                label="Username"
                                                value={formik.values.proxyConfig.auth.username}
                                                onChange={formik.handleChange}
                                                size="small"
                                                multiline
                                                rows={1}
                                                autoComplete="off"
                                                sx={{ flex: 1 }}
                                            />

                                            <TextField
                                                name="proxyConfig.auth.password"
                                                label="Password"
                                                value={formik.values.proxyConfig.auth.password}
                                                onChange={formik.handleChange}
                                                size="small"
                                                multiline
                                                rows={1}
                                                autoComplete="off"
                                                sx={{ flex: 1 }}
                                            />
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </TabPanel>
                    </Box>
                </Box>

                {/* Right Panel - Response */}
                <Box
                    sx={{
                        width: '50%',
                        backgroundColor: 'white',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Tabs
                        value={responseTab}
                        onChange={(_, newValue) => setResponseTab(newValue)}
                        sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
                    >
                        <Tab
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    Response
                                    {response && <Chip label="200" size="small" color="success" variant="filled" />}
                                    {error && <Chip label="Error" size="small" color="error" variant="filled" />}
                                </Box>
                            }
                        />
                    </Tabs>

                    <Box sx={{ flexGrow: 1, p: 0 }}>
                        {!response && !error && !loading && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                    color: 'text.secondary',
                                    flexDirection: 'column',
                                    gap: 2,
                                }}
                            >
                                <SendIcon sx={{ fontSize: 64, opacity: 0.3 }} />
                                <Typography variant="h6">Send a request to get a response</Typography>
                            </Box>
                        )}

                        {loading && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                    flexDirection: 'column',
                                    gap: 2,
                                }}
                            >
                                <CircularProgress size={48} />
                                <Typography variant="h6" color="primary">
                                    Sending request...
                                </Typography>
                            </Box>
                        )}

                        {error && (
                            <Box>
                                <Alert severity="error" sx={{ mb: 3 }}>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        Request Failed
                                    </Typography>
                                    <Typography>{error}</Typography>
                                </Alert>
                            </Box>
                        )}

                        {response && (
                            <Box sx={{ height: '100%' }}>
                                <Box sx={{ mb: 0, display: 'flex', justifyContent: 'space-between', gap: 0 }}>
                                    <TextField
                                        label="Filter Keys"
                                        variant="outlined"
                                        size="small"
                                        value={filterText}
                                        onChange={e => setFilterText(e.target.value)}
                                        fullWidth
                                        sx={{
                                            mb: 0,
                                            mt: 0,
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: 'grey.50',
                                            },
                                            // no border
                                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                        }}
                                    />
                                    <div
                                        style={{
                                            backgroundColor: sortJson ? alpha(theme.palette.primary.main, 0.1) : theme.palette.grey[50],
                                        }}
                                    >
                                        <Checkbox
                                            icon={<Sort color="disabled" />}
                                            checkedIcon={<Sort color="primary" />}
                                            checked={sortJson}
                                            onChange={e => setSortJson(e.target.checked)}
                                            title="Sort JSON Keys"
                                            disableRipple
                                        />
                                    </div>
                                </Box>
                                <Box
                                    sx={{
                                        border: 1,
                                        borderColor: 'divider',
                                        borderRadius: theme => theme.shape.borderRadius,
                                        height: 'calc(100% - 38px)',
                                    }}
                                >
                                    <Editor
                                        height="100%"
                                        defaultLanguage="json"
                                        value={renderResponse()}
                                        options={{
                                            readOnly: true,
                                            minimap: { enabled: false },
                                            scrollBeyondLastLine: false,
                                            fontSize: 14,
                                            wordWrap: 'on',
                                            automaticLayout: true,
                                            tabSize: 2,
                                        }}
                                        theme="vs-light"
                                    />
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </PageContainer>
    );
};

export default AdminOnlyFansApiTester;
